/**
 * Enharmonic spelling utilities for context-aware note naming
 * Based on music theory best practices and key signatures
 */

export type AccidentalPreference = 'smart' | 'sharps' | 'flats' | 'both';

// Map of chromatic pitches to their sharp and flat spellings
const ENHARMONIC_MAP: Record<number, { sharp: string; flat: string }> = {
  0: { sharp: 'C', flat: 'C' },     // C
  1: { sharp: 'C#', flat: 'Db' },   // C#/Db  
  2: { sharp: 'D', flat: 'D' },     // D
  3: { sharp: 'D#', flat: 'Eb' },   // D#/Eb
  4: { sharp: 'E', flat: 'E' },     // E
  5: { sharp: 'F', flat: 'F' },     // F
  6: { sharp: 'F#', flat: 'Gb' },   // F#/Gb
  7: { sharp: 'G', flat: 'G' },     // G
  8: { sharp: 'G#', flat: 'Ab' },   // G#/Ab
  9: { sharp: 'A', flat: 'A' },     // A
  10: { sharp: 'A#', flat: 'Bb' },  // A#/Bb
  11: { sharp: 'B', flat: 'B' }     // B
};

// Key signatures following circle of fifths
const KEY_SIGNATURES = {
  // Sharp keys
  'C': [],
  'G': ['F#'],
  'D': ['F#', 'C#'],
  'A': ['F#', 'C#', 'G#'],
  'E': ['F#', 'C#', 'G#', 'D#'],
  'B': ['F#', 'C#', 'G#', 'D#', 'A#'],
  'F#': ['F#', 'C#', 'G#', 'D#', 'A#', 'E#'],
  'C#': ['F#', 'C#', 'G#', 'D#', 'A#', 'E#', 'B#'],
  
  // Flat keys  
  'F': ['Bb'],
  'Bb': ['Bb', 'Eb'],
  'Eb': ['Bb', 'Eb', 'Ab'],
  'Ab': ['Bb', 'Eb', 'Ab', 'Db'],
  'Db': ['Bb', 'Eb', 'Ab', 'Db', 'Gb'],
  'Gb': ['Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'],
  'Cb': ['Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb', 'Fb']
};

// Sharp and flat key classifications
const SHARP_KEYS = ['G', 'D', 'A', 'E', 'B', 'F#', 'C#'];
const FLAT_KEYS = ['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'];

/**
 * Determines whether to use sharps or flats based on the root note and scale context
 */
function shouldUseFlats(rootNote: string, scaleType?: string): boolean {
  // If root note contains flat, prefer flats
  if (rootNote.includes('b')) {
    return true;
  }
  
  // If root note contains sharp, prefer sharps
  if (rootNote.includes('#')) {
    return false;
  }
  
  // Check if root note is in flat keys
  if (FLAT_KEYS.includes(rootNote)) {
    return true;
  }
  
  // Check if root note is in sharp keys
  if (SHARP_KEYS.includes(rootNote)) {
    return false;
  }
  
  // Default to sharps for neutral keys (C, Am, etc.)
  return false;
}

/**
 * Gets the contextually appropriate note name based on key signature
 */
export function getContextualNoteName(
  pitchClass: number, 
  rootNote: string, 
  scaleType?: string, 
  preference: AccidentalPreference = 'smart'
): string {
  const enharmonic = ENHARMONIC_MAP[pitchClass];
  
  if (!enharmonic) {
    return 'C'; // Fallback
  }
  
  // Handle natural notes (no accidentals)
  if (enharmonic.sharp === enharmonic.flat) {
    return enharmonic.sharp;
  }
  
  switch (preference) {
    case 'sharps':
      return enharmonic.sharp;
      
    case 'flats':
      return enharmonic.flat;
      
    case 'both':
      return `${enharmonic.sharp}/${enharmonic.flat}`;
      
    case 'smart':
    default:
      return shouldUseFlats(rootNote, scaleType) ? enharmonic.flat : enharmonic.sharp;
  }
}

/**
 * Gets both enharmonic spellings for tooltip display
 */
export function getEnharmonicPair(pitchClass: number): { primary: string; alternate: string | null } {
  const enharmonic = ENHARMONIC_MAP[pitchClass];
  
  if (!enharmonic || enharmonic.sharp === enharmonic.flat) {
    return { primary: enharmonic?.sharp || 'C', alternate: null };
  }
  
  return {
    primary: enharmonic.sharp,
    alternate: enharmonic.flat
  };
}

/**
 * Gets the appropriate accidental symbol for display
 */
export function getAccidentalSymbol(noteName: string): 'sharp' | 'flat' | null {
  if (noteName.includes('#')) {
    return 'sharp';
  } else if (noteName.includes('b')) {
    return 'flat';
  }
  return null;
}

/**
 * Converts a note name to its pitch class number (0-11)
 */
export function noteToPitchClass(noteName: string): number {
  const baseNote = noteName.charAt(0).toUpperCase();
  const accidental = noteName.slice(1);
  
  const baseNotes = { 'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11 };
  let pitchClass = baseNotes[baseNote as keyof typeof baseNotes] || 0;
  
  if (accidental.includes('#')) {
    pitchClass = (pitchClass + 1) % 12;
  } else if (accidental.includes('b')) {
    pitchClass = (pitchClass - 1 + 12) % 12;
  }
  
  return pitchClass;
}

export default {
  getContextualNoteName,
  getEnharmonicPair,
  getAccidentalSymbol,
  noteToPitchClass
};